<template>
  <section class="admin-content" id="manage-staging">
    <Navbar />
    <main class="admin-main">

      <!-- Header -->
      <div class="staging-header">
        <div class="container">
          <div class="row align-items-center p-t-40 p-b-40">
            <div class="col-md-8 text-white">
              <h3 class="h3 mb-1 font-weight-bold">Enrollment Staging</h3>
              <p class="mb-0 opacity-75">Review and verify pending enrollees before activation</p>
            </div>
            <div class="col-md-4 text-md-right mt-3 mt-md-0">
              <div class="d-flex justify-content-md-end gap-2">
                <span class="stat-badge">
                  <i class="fe fe-clock"></i>
                  {{ totalPending }} Pending
                </span>
                <button class="btn btn-outline-light btn-sm ml-2" @click="fetchData(currentPage)">
                  <i class="fe fe-refresh-cw"></i>
                </button>
                <button class="btn btn-outline-light btn-sm ml-2" @click="startExport" :disabled="exporting">
                  <i class="fe fe-loader mr-1" v-if="exporting"></i>
                  <i class="fe fe-download mr-1" v-else></i>
                  {{ exporting ? `Exporting… (${exportPagesLoaded})` : 'Export to Excel' }}
                </button>
                <button class="btn btn-success btn-sm ml-2" v-if="exportRows.length && !exporting">
                  <download-excel :data="exportRows" :fields="export_fields" type="xls"
                    :escapeCsv="false" :name="'enrollment_staging.xls'">
                    <i class="fe fe-file-text mr-1"></i> Download ({{ exportRows.length }})
                  </download-excel>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Filter Card -->
      <div class="container mt-n3 mb-4">
        <div class="card filter-card shadow">
          <div class="card-body py-3">
            <div class="row align-items-end">
              <div class="form-group col-md-3 mb-2 mb-md-0">
                <label class="small text-muted mb-1"><i class="fe fe-user mr-1"></i>First Name</label>
                <input type="text" class="form-control form-control-sm" v-model="firstname"
                  placeholder="Search first name..." />
              </div>
              <div class="form-group col-md-3 mb-2 mb-md-0">
                <label class="small text-muted mb-1"><i class="fe fe-user mr-1"></i>Last Name</label>
                <input type="text" class="form-control form-control-sm" v-model="lastname"
                  placeholder="Search last name..." />
              </div>
              <div class="form-group col-md-3 mb-2 mb-md-0">
                <label class="small text-muted mb-1"><i class="fe fe-credit-card mr-1"></i>Program</label>
                <v-select v-model="selected_sectors" :options="sectors" :reduce="s => s.name" multiple label="name"
                  placeholder="All programs" class="v-select-sm" />
              </div>
              <div class="form-group col-md-3 mb-2 mb-md-0">
                <label class="small text-muted mb-1"><i class="fe fe-map-pin mr-1"></i>LGA</label>
                <select class="form-control form-control-sm" v-model="localgovt">
                  <option value="">All LGAs</option>
                  <option v-for="lga in lga_states" :key="lga.id" :value="lga.id">{{ lga.local_name }}</option>
                </select>
              </div>
            </div>
            <div class="row align-items-end mt-2">
              <div class="form-group col-md-3 mb-2 mb-md-0">
                <label class="small text-muted mb-1"><i class="fe fe-calendar mr-1"></i>Start Date</label>
                <input type="date" class="form-control form-control-sm" v-model="from" @change="pushDate" />
              </div>
              <div class="form-group col-md-3 mb-2 mb-md-0">
                <label class="small text-muted mb-1"><i class="fe fe-calendar mr-1"></i>End Date</label>
                <input type="date" class="form-control form-control-sm" v-model="to" @change="pushDate" />
              </div>
            </div>
            <div class="row mt-2">
              <div class="col-md-10">
                <button @click="fetchData(1)" class="btn btn-dark btn-sm px-4">
                  <i class="fe fe-filter mr-1"></i> Apply Filter
                </button>
                <button @click="clearFilter" class="btn btn-outline-secondary btn-sm ml-2">
                  <i class="fe fe-x mr-1"></i> Clear
                </button>
              </div>
              <div class="col-md-2 text-right">
                <select class="form-control form-control-sm" v-model="perPage" @change="fetchData(1)">
                  <option value="15">15 / page</option>
                  <option value="30">30 / page</option>
                  <option value="50">50 / page</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Two-panel layout -->
      <div class="container mb-5">
        <div class="row">

          <!-- Left: Enrollee List -->
          <div class="col-lg-7 col-md-6 mb-4">
            <div class="card shadow-sm h-100">
              <div class="card-header d-flex justify-content-between align-items-center">
                <span class="font-weight-semibold">
                  <i class="fe fe-users mr-2 text-muted"></i>Pending Enrollees
                </span>
                <span class="badge badge-pill badge-warning">
                  {{ clients.data ? clients.data.length : 0 }} shown
                </span>
              </div>

              <div class="card-body p-0">
                <div class="table-responsive">
                  <table class="table table-hover enrollee-table mb-0">
                    <thead class="thead-light">
                      <tr>
                        <th>Enrollee</th>
                        <th>Phone</th>
                        <th>Program</th>
                        <th>Type</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="!clients.data || clients.data.length === 0">
                        <td colspan="5" class="text-center py-5 text-muted">
                          <i class="fe fe-inbox empty-icon"></i>
                          <p class="mt-2 mb-0">No pending enrollees found</p>
                        </td>
                      </tr>
                      <tr v-for="client in clients.data" :key="client.id"
                        :class="{ 'row-selected': selected && selected.id === client.id }" class="enrollee-row"
                        @click="selectEnrollee(client)">
                        <td>
                          <div class="d-flex align-items-center">
                            <div class="avatar avatar-sm mr-2 flex-shrink-0">
                              <img :src="client.image_url" class="avatar-img rounded-circle"
                                onerror="this.onerror=null; this.src='/assets/img/zamfara.jpg'" />
                            </div>
                            <span class="font-weight-medium">{{ client.full_name }}</span>
                          </div>
                        </td>
                        <td class="text-muted small">{{ client.phone_number }}</td>
                        <td>
                          <span class="badge badge-soft-primary sector-badge">{{ client.sector }}</span>
                        </td>
                        <td class="small text-muted">{{ client.account_type }}</td>
                        <td class="text-right">
                          <i class="fe fe-chevron-right text-muted"></i>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div class="card-footer">
                <b-pagination v-model="currentPage" :total-rows="rows" :per-page="perPage"
                  aria-controls="enrollee-table" @input="fetchData(currentPage)" size="sm" class="mb-0" />
              </div>
            </div>
          </div>

          <!-- Right: Detail Panel -->
          <div class="col-lg-5 col-md-6 mb-4">
            <div class="card shadow-sm detail-panel">
              <div class="card-header">
                <span class="font-weight-semibold">
                  <i class="fe fe-user-check mr-2 text-muted"></i>Enrollee Details
                </span>
              </div>

              <div class="card-body">
                <!-- Empty state -->
                <div class="empty-detail text-center py-5" v-if="!selected">
                  <div class="empty-icon-wrap mb-3">
                    <i class="fe fe-mouse-pointer"></i>
                  </div>
                  <p class="text-muted mb-0">Select an enrollee from the list<br />to review their details</p>
                </div>

                <!-- Detail content -->
                <div v-if="selected">
                  <!-- Avatar + name -->
                  <div class="text-center mb-4">
                    <div class="enrollee-avatar mx-auto mb-3">
                      <img :src="selected.image_url" class="rounded-circle"
                        onerror="this.onerror=null; this.src='/assets/img/zamfara.jpg'" />
                    </div>
                    <h5 class="mb-1 font-weight-bold">{{ selected.full_name }}</h5>
                    <span class="badge badge-warning">Inactive</span>
                  </div>

                  <!-- Info rows -->
                  <div class="info-grid">
                    <div class="info-row">
                      <span class="info-label"><i class="fe fe-hash mr-1"></i>Program</span>
                      <strong class="info-value">{{ selected.sector }}</strong>
                    </div>
                    <div class="info-row">
                      <span class="info-label"><i class="fe fe-users mr-1"></i>Account Type</span>
                      <span class="info-value">{{ selected.account_type }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-label"><i class="fe fe-credit-card mr-1"></i>Program</span>
                      <span class="badge badge-soft-primary info-value">{{ selected.sector }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-label"><i class="fe fe-phone mr-1"></i>Phone</span>
                      <span class="info-value">{{ selected.phone_number || '—' }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-label"><i class="fe fe-activity mr-1"></i>Gender</span>
                      <span class="info-value">{{ selected.gender || '—' }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-label"><i class="fe fe-calendar mr-1"></i>Date of Birth</span>
                      <span class="info-value">{{ selected.dob || '—' }}</span>
                    </div>
                  </div>

                  <!-- Action buttons -->
                  <div class="row mt-4">
                    <div class="col-6">
                      <button class="btn btn-success btn-block" @click="activateUser(selected)">
                        <i class="fe fe-check-circle mr-1"></i> Activate
                      </button>
                    </div>
                    <div class="col-6">
                      <button class="btn btn-danger btn-block" @click="deactivateUser(selected)">
                        <i class="fe fe-x-circle mr-1"></i> Disable
                      </button>
                    </div>
                  </div>
                  <div class="mt-2">
                    <router-link
                      :to="{ path: (selected.account_type === 'Principal' ? '/client/' : '/dependent/') + selected.id }">
                      <button class="btn btn-outline-info btn-block">
                        <i class="fe fe-eye mr-1"></i> View Full Profile
                      </button>
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <div class="vld-parent">
        <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage" />
      </div>
    </main>
  </section>
</template>

<script>
  import Navbar from "@/views/Navbar.vue";
  import Loading from "vue-loading-overlay";
  import "vue-loading-overlay/dist/vue-loading.css";
  import sectorsJson from "@/json/sectors_dynamic.json";
import moment from "moment";
moment().format();


  let agency = JSON.parse(localStorage.getItem("agency"));

  export default {
    components: { Navbar, Loading },
    data() {
      return {
        agency,
        isLoading: false,
        fullPage: true,
        sectors: [],
        selected_sectors: [],
        lga_states: [],
        clients: { meta: {}, data: [] },
        selected: null,
        firstname: "",
        lastname: "",
        localgovt: "",
        date: "",
       from: moment().subtract(60, "days").format("YYYY-MM-DD"),
      to: moment().format("YYYY-MM-DD"),
        currentPage: 1,
        rows: 0,
        perPage: 15,
        exporting: false,
        exportPagesLoaded: 0,
        exportRows: [],
        export_fields: {
          "Full Name": "full_name",
          "Phone Number": "phone_number",
          "Program": "sector",
          "Account Type": "account_type",
          "Gender": "gender",
          "Date of Birth": "dob",
        },
      };
    },
    computed: {
      totalPending() {
        return this.clients.meta ? (this.clients.meta.total_principal || 0) + (this.clients.meta.total_dep || 0) : 0;
      },
    },
    methods: {
      fetchData(page) {
        this.isLoading = true;
        this.selected = null;
        this.axios
          .get(`/api/v1/auth/getUsersfilterparams`, {
            params: {
              page: page || 1,
              agency_id: agency.id,
              perPage: this.perPage,
              enabled_user: false,
              firstname: this.firstname || "",
              lastname: this.lastname || "",
              localgovt: this.localgovt || "",
              date: this.date || "",
              from: this.from || "",
              to: this.to || "",
              sectors: this.selected_sectors.length ? this.selected_sectors : "",
            },
          })
          .then((res) => {
            this.clients = res.data;
            this.rows = res.data.meta.total_principal;
            this.isLoading = false;
          })
          .catch((err) => {
            console.error(err);
            this.isLoading = false;
          });
      },
      activateUser(client) {
        if (!confirm(`Activate ${client.full_name}?`)) return;
        this.isLoading = true;
        this.axios
          .post(`/api/v1/auth/enableUser/${client.id}`)
          .then(() => {
            this.$toasted.success(`${client.full_name} activated successfully`, {
              position: "top-center",
              duration: 3000,
            });
            this.selected = null;
            this.fetchData(this.currentPage);
          })
          .catch((err) => {
            console.error(err);
            this.$toasted.error("Failed to activate user", { position: "top-center", duration: 3000 });
            this.isLoading = false;
          });
      },
      deactivateUser(client) {
        if (!confirm(`Disable ${client.full_name}?`)) return;
        this.isLoading = true;
        this.axios
          .post(`/api/v1/auth/disableUser/${client.id}`)
          .then(() => {
            this.$toasted.info(`${client.full_name} has been disabled`, {
              position: "top-center",
              duration: 3000,
            });
            this.selected = null;
            this.fetchData(this.currentPage);
          })
          .catch((err) => {
            console.error(err);
            this.$toasted.error("Failed to disable user", { position: "top-center", duration: 3000 });
            this.isLoading = false;
          });
      },
      selectEnrollee(client) {
        this.selected = client;
      },
      pushDate() {
        this.date = "date";
      },
      clearFilter() {
        this.firstname = "";
        this.lastname = "";
        this.localgovt = "";
        this.date = "";
        this.from = "";
        this.to = "";
        this.selected_sectors = [];
        this.fetchData(1);
      },
      getLGA() {
        this.axios
          .get(`/api/v1/auth/lga/${agency.state}`)
          .then((res) => { this.lga_states = res.data.data; })
          .catch(console.error);
      },
      async startExport() {
        this.exporting = true;
        this.exportPagesLoaded = 0;
        this.exportRows = [];
        try {
          this.exportRows = await this.fetchAllStagingForExport();
          if (!this.exportRows.length) {
            this.$toasted.info("No records to export for the current filters", {
              position: "top-center",
              duration: 3000,
            });
          }
        } catch (error) {
          console.error(error);
          this.$toasted.error("Failed to export data", { position: "top-center", duration: 3000 });
        } finally {
          this.exporting = false;
        }
      },
      async fetchAllStagingForExport() {
        const perPage = 500;
        const maxPages = 50;
        let page = 1;
        const all = [];
        // Stop once a page returns fewer rows than requested — cheaper than
        // relying on pagination meta whose exact shape isn't guaranteed here.
        while (page <= maxPages) {
          const res = await this.axios.get(`/api/v1/auth/getUsersfilterparams`, {
            params: {
              page,
              agency_id: agency.id,
              perPage,
              enabled_user: false,
              firstname: this.firstname || "",
              lastname: this.lastname || "",
              localgovt: this.localgovt || "",
              sectors: this.selected_sectors.length ? this.selected_sectors : "",
            },
          });
          const pageData = (res.data && res.data.data) || [];
          all.push(...pageData);
          this.exportPagesLoaded = page;
          if (pageData.length < perPage) break;
          page++;
        }
        return all;
      },
    },
    mounted() {
      const allSectors = sectorsJson;
      this.sectors = allSectors.filter((s) => s.agency_id == agency.id);
    },
    created() {
      this.fetchData(1);
      this.getLGA();
    },
  };
</script>

<style scoped>
  .staging-header {
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 60%, #0f3460 100%);
  }

  .stat-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(255, 193, 7, 0.2);
    color: #ffc107;
    border: 1px solid rgba(255, 193, 7, 0.4);
    border-radius: 20px;
    padding: 6px 14px;
    font-size: 0.85rem;
    font-weight: 600;
  }

  .filter-card {
    border: none;
    border-radius: 10px;
  }

  .enrollee-table th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #6c757d;
    border-top: none;
  }

  .enrollee-row {
    cursor: pointer;
    transition: background 0.15s ease;
  }

  .enrollee-row:hover {
    background-color: #f8f9ff;
  }

  .row-selected {
    background-color: #eef2ff !important;
    border-left: 3px solid #4f46e5;
  }

  .sector-badge {
    font-size: 0.7rem;
    max-width: 140px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: inline-block;
  }

  .empty-icon {
    font-size: 2.5rem;
    color: #dee2e6;
  }

  .detail-panel {
    border: none;
    border-radius: 10px;
  }

  .empty-detail {
    min-height: 280px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .empty-icon-wrap {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: #f1f3f5;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    color: #adb5bd;
  }

  .enrollee-avatar img {
    width: 80px;
    height: 80px;
    object-fit: cover;
    border: 3px solid #e9ecef;
    border-radius: 50%;
  }

  .info-grid {
    border: 1px solid #f1f3f5;
    border-radius: 8px;
    overflow: hidden;
  }

  .info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 14px;
    border-bottom: 1px solid #f1f3f5;
  }

  .info-row:last-child {
    border-bottom: none;
  }

  .info-label {
    font-size: 0.8rem;
    color: #6c757d;
  }

  .info-value {
    font-size: 0.85rem;
    text-align: right;
    max-width: 55%;
    word-break: break-word;
  }

  .font-weight-medium {
    font-weight: 500;
  }

  .font-weight-semibold {
    font-weight: 600;
  }

  .badge-soft-primary {
    background-color: #e8f0fe;
    color: #3d6adf;
  }
</style>